import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchClientSubscriptions,
  createClientSubscription,
  updateClientSubscription,
  deleteClientSubscription,
  pauseClientSubscription,
  activateClientSubscription,
  generatePaymentLink,
  tryChargeSubscription,
  tryChargeSubscriptionWithCard,
  fetchClientSubscriptionDetails,
  fetchClientSubscriptionUsage,
  fetchSubscribersStats,
  destroyClientSubscription,
  deleteClientSubscriptionViaPut,
  markClientSubscriptionExpired,
} from "./thunks";
import { toast } from "react-toastify";

interface ClientSubscription {
  id: string | number;
  client_id: string | number;
  subscription_id: string | number;
  // Adicione mais campos conforme necessário
}

interface PaginatedClientSubscriptions {
  data: ClientSubscription[];
  total?: number;
  current_page?: number;
  per_page?: number;
  last_page?: number;
  from?: number;
  to?: number;
}

interface ClientSubscriptionsState {
  clientSubscriptions: PaginatedClientSubscriptions;
  subscriptionDetails: any | null;
  usageHistory: any[] | null;
  subscribersStats: any | null;
  paymentLink: string;
  loading: boolean;
  detailsLoading: boolean;
  usageLoading: boolean;
  statsLoading: boolean;
  isSubmittingPaymentMethod: boolean;
  error: string | null;
}

const initialState: ClientSubscriptionsState = {
  clientSubscriptions: { data: [] },
  subscriptionDetails: null,
  usageHistory: null,
  subscribersStats: null,
  paymentLink: "",
  loading: false,
  detailsLoading: false,
  usageLoading: false,
  statsLoading: false,
  isSubmittingPaymentMethod: false,
  error: null,
};

const clientSubscriptionsSlice = createSlice({
  name: "clientSubscriptions",
  initialState,
  reducers: {
    clearClientSubscriptions: (state) => {
      state.clientSubscriptions = { data: [] };
      state.loading = false;
      state.error = null;
    },
    clearSubscriptionDetails: (state) => {
      state.subscriptionDetails = null;
      state.detailsLoading = false;
    },
    clearUsageHistory: (state) => {
      state.usageHistory = null;
      state.usageLoading = false;
    },
    clearSubscribersStats: (state) => {
      state.subscribersStats = null;
      state.statsLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchClientSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchClientSubscriptions.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          // Suporte ao novo formato da API
          if (payload && payload.list) {
            const pagination = payload.pagination || {};
            state.clientSubscriptions = {
              data: payload.list,
              total: pagination.total,
              current_page: pagination.current_page,
              per_page: pagination.per_page,
              last_page: pagination.last_page,
              from: pagination.from,
              to: pagination.to,
            } as any;
            // Armazena contadores nos subscribersStats para reutilização nos cards
            state.subscribersStats = payload.counts || null;
          } else if (Array.isArray(payload?.data)) {
            state.clientSubscriptions = { data: payload.data };
          } else {
            state.clientSubscriptions = {
              data: payload?.data?.data,
              total: payload?.data?.total,
              current_page: payload?.data?.current_page,
              per_page: payload?.data?.per_page,
              last_page: payload?.data?.last_page,
              from: payload?.data?.from,
              to: payload?.data?.to,
            } as any;
          }
        },
      )
      .addCase(fetchClientSubscriptions.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro na busca. Tente novamente.");
      })

      .addCase(fetchClientSubscriptionDetails.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })
      .addCase(
        fetchClientSubscriptionDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.detailsLoading = false;
          state.subscriptionDetails = action.payload.data;
        },
      )
      .addCase(fetchClientSubscriptionDetails.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.detailsLoading = false;
        //toast.error("Erro ao carregar detalhes da assinatura");
      })

      .addCase(fetchClientSubscriptionUsage.pending, (state) => {
        state.usageLoading = true;
        state.error = null;
      })
      .addCase(
        fetchClientSubscriptionUsage.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.usageLoading = false;
          state.usageHistory = action.payload.data;
        },
      )
      .addCase(fetchClientSubscriptionUsage.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.usageLoading = false;
        toast.error("Erro ao carregar histórico de utilização");
      })

      .addCase(
        generatePaymentLink.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.paymentLink = action.payload.data;
        },
      )
      .addCase(generatePaymentLink.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Não foi possível criar o agendamento já existe um agendamento muito próximo para este funcionário, tente novamente!",
        );
      })

      .addCase(tryChargeSubscription.pending, (state) => {
        state.isSubmittingPaymentMethod = true;
      })
      .addCase(
        tryChargeSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isSubmittingPaymentMethod = false;
          toast.success(
            action?.payload?.message ?? "Aguarde confirmação do banco!",
          );
        },
      )
      .addCase(tryChargeSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.isSubmittingPaymentMethod = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Erro ao tentar cobrar",
        );
      })

      .addCase(tryChargeSubscriptionWithCard.pending, (state) => {
        state.isSubmittingPaymentMethod = true;
      })
      .addCase(
        tryChargeSubscriptionWithCard.fulfilled,
        (state, action: PayloadAction<any>) => {
          console.log("tryChargeSubscriptionWithCard.fulfilled");
          state.isSubmittingPaymentMethod = false;
          toast.success(
            action?.payload?.message ??
              "Cobrança com cartão realizada! Aguarde confirmação do banco!",
          );
        },
      )
      .addCase(tryChargeSubscriptionWithCard.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.isSubmittingPaymentMethod = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Erro ao tentar cobrar com cartão",
        );
      })

      // Create
      .addCase(createClientSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createClientSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.clientSubscriptions.data.push(action.payload.data);
          toast.success("Assinatura criada com sucesso!");
        },
      )
      .addCase(createClientSubscription.rejected, (state, action) => {
        const payload = action.payload as any;
        console.log("payload", payload?.error?.error);

        state.error = payload?.error?.error || "Erro desconhecido";
        state.loading = false;
        const errorMessage =
          payload?.error?.error || "Erro ao criar assinatura. Tente novamente!";
        toast.error(errorMessage);
      })

      // Update
      .addCase(updateClientSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateClientSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.clientSubscriptions.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.clientSubscriptions.data[index] = action.payload.data;
          }
          toast.success("Assinatura atualizada com sucesso!");
        },
      )
      .addCase(updateClientSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao atualizar assinatura!");
      })
      .addCase(pauseClientSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        pauseClientSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.clientSubscriptions.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.clientSubscriptions.data[index] = action.payload.data;
          }
          toast.success("Assinatura pausada com sucesso!");
        },
      )
      .addCase(pauseClientSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao pausar assinatura!");
      })
      .addCase(activateClientSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        activateClientSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.clientSubscriptions.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.clientSubscriptions.data[index] = action.payload.data;
          }
          toast.success("Assinatura ativada com sucesso!");
        },
      )
      .addCase(activateClientSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao ativar assinatura!");
      })

      // Delete via PUT alias
      .addCase(deleteClientSubscriptionViaPut.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteClientSubscriptionViaPut.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.clientSubscriptions.data.findIndex(
            (s) => String(s.id) === String(action.payload.data.id),
          );
          if (index !== -1) {
            state.clientSubscriptions.data[index] = action.payload.data;
          }
          toast.success("Assinatura excluída com sucesso!");
        },
      )
      .addCase(deleteClientSubscriptionViaPut.rejected, (state, action) => {
        state.error = (action.payload as { error: string })?.error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string })?.error ||
            "Erro ao excluir assinatura!",
        );
      })

      // Mark as expired
      .addCase(markClientSubscriptionExpired.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        markClientSubscriptionExpired.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.clientSubscriptions.data.findIndex(
            (s) => String(s.id) === String(action.payload.data.id),
          );
          if (index !== -1) {
            state.clientSubscriptions.data[index] = action.payload.data;
          }
          toast.success("Assinatura marcada como vencida com sucesso!");
        },
      )
      .addCase(markClientSubscriptionExpired.rejected, (state, action) => {
        state.error = (action.payload as { error: string })?.error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string })?.error ||
            "Erro ao marcar como vencida!",
        );
      })

      // Delete
      .addCase(deleteClientSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteClientSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.clientSubscriptions.data.findIndex(
            (s) => String(s.id) === String(action.payload.data.id),
          );
          if (index !== -1) {
            state.clientSubscriptions.data[index] = action.payload.data;
          }
          toast.success("Assinatura cancelada com sucesso!");
        },
      )
      .addCase(deleteClientSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao excluir assinatura!");
      })

      // Permanent delete (destroy)
      .addCase(destroyClientSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        destroyClientSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.clientSubscriptions.data.findIndex(
            (s) => String(s.id) === String(action.payload.data.id),
          );
          if (index !== -1) {
            // remove from list
            state.clientSubscriptions.data.splice(index, 1);
          }
          toast.success("Assinatura excluída com sucesso!");
        },
      )
      .addCase(destroyClientSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string })?.error;
        state.loading = false;
        const msg =
          (action.payload as { error: string })?.error ||
          "Erro ao excluir assinatura!";
        toast.error(msg);
      })

      // Fetch Subscribers Stats
      .addCase(fetchSubscribersStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(
        fetchSubscribersStats.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.statsLoading = false;
          state.subscribersStats = action.payload.data;
        },
      )
      .addCase(fetchSubscribersStats.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.statsLoading = false;
        toast.error("Erro ao buscar estatísticas!");
      });
  },
});

export const {
  clearClientSubscriptions,
  clearSubscriptionDetails,
  clearUsageHistory,
  clearSubscribersStats,
} = clientSubscriptionsSlice.actions;
export default clientSubscriptionsSlice.reducer;
